import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { TableToolbar } from '@/components/common/TableToolbar';
import { useTableQuery } from '@/hooks/useTableQuery';
import { PartnerGrid } from '@/features/trusted-partners/components/PartnerGrid';
import { useDirectory, usePartnerCategories } from '@/features/trusted-partners/hooks/useTrustedPartners';
import {
  DIRECTORY_FILTER_CATEGORY,
  DIRECTORY_PAGE_SIZE,
  TRUSTED_PARTNERS_STRINGS as S,
} from '@/features/trusted-partners/trusted-partners.constants';

// Module-level so the array identities stay stable across renders (`useTableQuery`
// memoizes on them). No sort: the directory has one fixed order (featured, then name).
const FILTER_KEYS = [DIRECTORY_FILTER_CATEGORY] as const;
const SORT_KEYS: readonly string[] = [];

/**
 * Full paginated Partner Directory (FR-111 "View All Partners"): category
 * filter and name search, both held in the URL so a filtered view survives a
 * refresh and the back button. The page size is fixed here, never read from the
 * URL, because the Backend rejects an oversized `pageSize` outright.
 */
export function TrustedPartnersDirectoryPage() {
  const table = useTableQuery({ filterKeys: FILTER_KEYS, sortKeys: SORT_KEYS, defaultPageSize: DIRECTORY_PAGE_SIZE });
  const { state } = table;
  const categoryId = state.filters[DIRECTORY_FILTER_CATEGORY]?.[0];

  const categories = usePartnerCategories();
  const directory = useDirectory({
    page: state.page,
    pageSize: DIRECTORY_PAGE_SIZE,
    ...(state.search ? { search: state.search } : {}),
    ...(categoryId ? { categoryId } : {}),
  });
  const partners = directory.data?.data ?? [];
  const meta = directory.data?.meta;

  return (
    <div className="min-h-full bg-page-bg">
      {/* No breadcrumb, matching the Trusted Partners and Startup Perks pages:
          the Network menu is the way between them (UX sheet v2). */}
      <PageHeader title={S.DIRECTORY_PAGE_TITLE} />
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8">
        <TableToolbar search={state.search} onSearchChange={table.setSearch}>
          <Select
            label={S.CATEGORY_LABEL}
            placeholder={S.CATEGORY_ALL}
            size="sm"
            value={categoryId ?? ''}
            options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
            onChange={(event) => table.setFilter(DIRECTORY_FILTER_CATEGORY, event.target.value ? [event.target.value] : [])}
          />
        </TableToolbar>

        {directory.isPending && (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        )}
        {directory.isError && <ErrorState description={S.DIRECTORY_LOAD_FAILED} onRetry={() => void directory.refetch()} />}
        {directory.isSuccess && partners.length === 0 && (
          <EmptyState title={S.NO_MATCH_TITLE} description={S.NO_MATCH_DESCRIPTION} />
        )}
        {directory.isSuccess && partners.length > 0 && meta && (
          <>
            <PartnerGrid partners={partners} />
            <Pagination
              page={meta.page}
              pageSize={meta.pageSize}
              total={meta.total}
              totalPages={meta.totalPages}
              onPageChange={table.setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
